import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import t from '../theme';

export default function ClassesPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [classes,  setClasses]  = useState([]);
  const [students, setStudents] = useState([]);
  const [lessons,  setLessons]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editClass, setEditClass] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', student_ids: [] });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState('');

  const fetchClasses  = useCallback(async () => { try { const r = await axios.get('/api/classes'); setClasses(r.data); } catch(e){} finally { setLoading(false); } }, []);
  const fetchStudents = useCallback(async () => { if (isAdmin) { try { const r = await axios.get('/api/students'); setStudents(r.data); } catch(e){} } }, [isAdmin]);
  const fetchLessons  = useCallback(async () => { if (isAdmin) { try { const r = await axios.get('/api/lessons');   setLessons(r.data); console.log('lessons loaded:', r.data.length); } catch(e){ console.error('fetchLessons error:', e); } } }, [isAdmin]);
  useEffect(() => { fetchClasses(); fetchStudents(); fetchLessons(); }, [fetchClasses, fetchStudents, fetchLessons]);

  const openAdd = () => { setEditClass(null); setForm({ title: '', description: '', student_ids: [], lesson_ids: [] }); setFormError(''); setModalOpen(true); };
  const openEdit = c => { console.log('openEdit lessonItems:', c.lessonItems); setEditClass(c); setForm({ title: c.title, description: c.description || '', student_ids: c.students?.map(s => s.id) || [], lesson_ids: c.lessonItems?.map(l => l.id) || [] }); setFormError(''); setModalOpen(true); };
  const toggleStudent = id => setForm(f => ({ ...f, student_ids: f.student_ids.includes(id) ? f.student_ids.filter(x => x !== id) : [...f.student_ids, id] }));
  const toggleLesson  = id => setForm(f => ({ ...f, lesson_ids:  f.lesson_ids.includes(id)  ? f.lesson_ids.filter(x => x !== id)  : [...f.lesson_ids,  id] }));

  const handleSave = async () => {
    if (!form.title.trim()) return setFormError('Title is required.');
    setSaving(true);
    try {
      if (editClass) await axios.put(`/api/classes/${editClass.id}`, form);
      else await axios.post('/api/classes', form);
      setModalOpen(false); fetchClasses();
    } catch (e) { setFormError(e.response?.data?.error || 'Failed to save.'); }
    finally { setSaving(false); }
  };

  const filtered = classes.filter(c => c.title.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: '22px', fontWeight: 600, color: t.text, letterSpacing: '-0.3px', margin: 0 }}>{isAdmin ? 'Classes' : 'My Classes'}</h2>
          <p style={{ fontSize: '13px', color: t.text3, margin: '4px 0 0' }}>{classes.length} class{classes.length !== 1 ? 'es' : ''}</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input style={{ ...t.input, width: '220px' }} placeholder="Search classes..." value={search} onChange={e => setSearch(e.target.value)} />
          {isAdmin && <button style={t.btnPrimary} onClick={openAdd}>+ New Class</button>}
        </div>
      </div>

      {loading ? <div style={{ textAlign: 'center', color: t.text3, padding: '48px' }}>Loading...</div>
        : filtered.length === 0 ? (
          <div style={{ ...t.card, padding: '48px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.3 }}>◈</div>
            <p style={{ color: t.text2, fontWeight: 500, marginBottom: '6px' }}>{isAdmin ? 'No classes yet' : 'No classes assigned'}</p>
            <p style={{ color: t.text3, fontSize: '13px' }}>{isAdmin ? 'Create your first class to get started.' : 'Your teacher will add you to classes.'}</p>
            {isAdmin && <button style={{ ...t.btnPrimary, marginTop: '16px' }} onClick={openAdd}>+ Create First Class</button>}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: '14px' }}>
            {filtered.map(cls => (
              <div key={cls.id} style={{ ...t.card, padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, color: t.text, margin: 0 }}>{cls.title}</h3>
                  {isAdmin && (
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button style={{ ...t.btnSecondary, padding: '4px 10px', fontSize: '12px' }} onClick={() => openEdit(cls)}>Edit</button>
                      <button style={{ ...t.btnDanger, padding: '4px 10px', fontSize: '12px' }} onClick={() => setDeleteTarget(cls)}>Del</button>
                    </div>
                  )}
                </div>
                {cls.description && <p style={{ fontSize: '13px', color: t.text2, margin: 0, lineHeight: 1.5 }}>{cls.description}</p>}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                  {cls.students?.map(s => (
                    <span key={s.id} style={{ background: t.surface2, border: `1px solid ${t.border}`, color: t.text2, padding: '2px 8px', borderRadius: '20px', fontSize: '11px' }}>{s.full_name}</span>
                  ))}
                </div>
                <button style={{ ...t.btnSecondary, marginTop: 'auto', textAlign: 'center' }} onClick={() => navigate(`/classes/${cls.id}`)}>Open Class →</button>
              </div>
            ))}
          </div>
        )}

      {modalOpen && (
        <div style={t.overlay} onClick={e => e.target === e.currentTarget && setModalOpen(false)}>
          <div style={{ ...t.modal, maxWidth: '560px' }}>
            <div style={t.modalHeader}>
              <h3 style={t.modalTitle}>{editClass ? 'Edit Class' : 'New Class'}</h3>
              <button style={t.closeBtn} onClick={() => setModalOpen(false)}>✕</button>
            </div>
            <div style={t.modalBody}>
              <div style={t.fieldGroup}>
                <label style={t.label}>Class Title *</label>
                <input style={t.input} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Beginner Tabla" />
              </div>
              <div style={t.fieldGroup}>
                <label style={t.label}>Description</label>
                <textarea style={t.textarea} rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional description..." />
              </div>
              <div style={t.fieldGroup}>
                <label style={t.label}>Assign Students {form.student_ids.length > 0 && <span style={{ color: t.accent }}>({form.student_ids.length} selected)</span>}</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', maxHeight: '200px', overflowY: 'auto' }}>
                  {students.map(s => {
                    const on = form.student_ids.includes(s.id);
                    return (
                      <div key={s.id} onClick={() => toggleStudent(s.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: t.r,
                          border: `1px solid ${on ? t.accent : t.border}`, background: on ? t.accentLight : t.surface2, cursor: 'pointer' }}>
                        <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: t.accentLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 600, color: t.accent }}>{s.full_name[0]}</div>
                        <span style={{ fontSize: '13px', fontWeight: 500, color: t.text, flex: 1 }}>{s.full_name}</span>
                        {on && <span style={{ color: t.accent, fontWeight: 700 }}>✓</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Lesson assignment */}
              <div style={t.fieldGroup}>
                <label style={t.label}>
                  Assign Lessons
                  {form.lesson_ids.length > 0 && <span style={{ background: t.accent, color: '#0e0e0d', borderRadius: '20px', padding: '1px 7px', fontSize: '10px', fontWeight: 700, marginLeft: '6px' }}>{form.lesson_ids.length}</span>}
                </label>
                {lessons.length === 0 ? (
                  <p style={{ fontSize: '12px', color: t.text3, fontStyle: 'italic', margin: 0 }}>No lessons in library yet.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', maxHeight: '220px', overflowY: 'auto' }}>
                    {lessons.map(l => {
                      const on = form.lesson_ids.includes(l.id);
                      return (
                        <div key={l.id} onClick={() => toggleLesson(l.id)}
                          style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px',
                            borderRadius: '8px', cursor: 'pointer',
                            border: `1px solid ${on ? t.accent : t.border}`,
                            background: on ? t.accentLight : t.surface2 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '13px', fontWeight: on ? 500 : 400, color: on ? t.text : t.text2 }}>{l.title}</div>
                            <div style={{ display: 'flex', gap: '4px', marginTop: '2px' }}>
                              {l.taal && <span style={{ fontSize: '10px', background: t.accentLight, color: t.accent, padding: '1px 6px', borderRadius: '20px' }}>{l.taal.name}</span>}
                              {l.lessonType && <span style={{ fontSize: '10px', background: t.surface3, color: t.text3, padding: '1px 6px', borderRadius: '20px' }}>{l.lessonType.name}</span>}
                            </div>
                          </div>
                          {on && <span style={{ color: t.accent, fontWeight: 700, flexShrink: 0 }}>✓</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {formError && <div style={t.errorBox}>{formError}</div>}
            </div>
            <div style={t.modalFooter}>
              <button style={t.btnSecondary} onClick={() => setModalOpen(false)}>Cancel</button>
              <button style={t.btnPrimary} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : editClass ? 'Save Changes' : 'Create Class'}</button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div style={t.overlay} onClick={e => e.target === e.currentTarget && setDeleteTarget(null)}>
          <div style={{ ...t.modal, maxWidth: '420px' }}>
            <div style={t.modalHeader}><h3 style={t.modalTitle}>Delete Class</h3><button style={t.closeBtn} onClick={() => setDeleteTarget(null)}>✕</button></div>
            <div style={{ ...t.modalBody, textAlign: 'center' }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>⚠</div>
              <p style={{ color: t.text2 }}>Delete <strong style={{ color: t.text }}>{deleteTarget.title}</strong>?</p>
            </div>
            <div style={t.modalFooter}>
              <button style={t.btnSecondary} onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button style={t.btnDanger} onClick={async () => { await axios.delete(`/api/classes/${deleteTarget.id}`); setDeleteTarget(null); fetchClasses(); }}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
